import { addLog } from 'assets/components/feedback/Feedback';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales, Language } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProviderPartition } from 'assets/providers/data/DataProvider';
import { combineStrings, replaceStringTemplateWithValues } from 'assets/utils/data/String';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import { cloneDeep, differenceBy, xorBy } from 'lodash';
import dealerGroupDeleteProductEnhancementDocumentApi from 'models/dealerManagement/dealerGroup/deleteProductEnhancementDocument/Api.DealerGroup.DeleteProductEnhancementDocument';
import productEnhancementDocumentListDealerGroupDocumentsApi from 'models/productManagement/productEnhancementDocument/listDealerGroups/Api.ProductEnhancementDocument.ListDealerGroups';
import { useEffect, useMemo, useState } from 'react';
import { productEnhancementIndexRoute } from '../../productEnhancement/ProductEnhancement.Index';
import { ProductEnhancementDealerGroupDocumentAddForm } from './sections/ProductEnhancementDocument.DealerGroupDocuments.Add';
import dealerGroupUploadProductEnhancementDocumentApi from 'models/dealerManagement/dealerGroup/uploadProductEnhancementDocument/Api.DealerGroup.UploadProductEnhancementDocument';
import {
  documentTranslationDeleteApi,
  documentTranslationListApi,
  documentTranslationSaveApi,
} from 'models/localization/documentTranslation/Model.DocumentTranslation';
import { convertToArray } from 'assets/utils/data/Data';

export enum ProductEnhancementDealerGroupDocumentsAction {
  ADD = 'add',
  DELETE = 'delete',
}
export type VerifyProductDocumentsAction = Utils.VerifyExtends<
  Module.ProductManagement.ProductDocument.Actions,
  Utils.ValueOf<typeof ProductEnhancementDealerGroupDocumentsAction>
>;
export default function useProductEnhancementDealerGroupDocument() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.ProductManagement.ProductEnhancementDocument.Params,
    Module.ProductManagement.ProductEnhancementDocument.Query
  >({
    route: productEnhancementIndexRoute,
  });
  const productEnhancementDocumentId = pageRouter.params.id ? parseInt(pageRouter.params.id) : null;

  const { lang } = getLocales();
  const [currentView, setView] = useState<ProductEnhancementDealerGroupDocumentsAction | undefined>();
  const [selectedItems, setSelectedItems] = useState<Model.IProductEnhancementDealerGroupDocument[]>([]);
  const [selectedTranslatedItems, setSelectedTranslatedItems] = useState<Model.IDocumentTranslation[]>([]);
  const [{ limit, offset, distributorId }, setDealerGroupDocumentFilters] = useState({
    limit: defaultLimit,
    offset: defaultOffset,
    distributorId: undefined,
  });

  const canView = permissions.EDIT_PRODUCT;
  const listDealerGroupDocumentsApi = useApi({
    action: productEnhancementDocumentListDealerGroupDocumentsApi,
    default: { productEnhancementDocumentId: productEnhancementDocumentId },
    body: { productEnhancementDocumentId } as Api.Core.ProductEnhancementDocument.ListDealerDocuments.IRequest,
    wait: !canView,
  });

  const documentIds = useMemo(
    () => listDealerGroupDocumentsApi.payload?.data.map((d) => d.id),
    [listDealerGroupDocumentsApi.payload]
  );
  const listDocumentTranslationsApi = useApi(
    {
      action: documentTranslationListApi,
      body: { documentType: 'ProductEnhancementDealerGroupDocument', documentId: documentIds },
      wait: !canView || !documentIds?.length,
    },
    [canView, documentIds]
  );
  function reloadTranslatedDocs() {
    listDocumentTranslationsApi.execute((b) => b);
  }

  const handleUploadDealerGroupDocumentSubmit = async (formData: ProductEnhancementDealerGroupDocumentAddForm) => {
    const dealerGroups = formData.dealerGroups || [];
    let uploadedFiles = 0;
    let totalFiles = 0;

    for (const dealerGroup of dealerGroups) {
      const docUploadResponse = await dealerGroupUploadProductEnhancementDocumentApi(
        {
          id: dealerGroup,
          productEnhancementId: formData.productEnhancementId,
          productEnhancementDocumentId: formData.productEnhancementDocumentId,
        },
        { formFile: formData.formFile, languageIsoCode: Language.english }
      );

      if (docUploadResponse.status === HttpStatus.ok) {
        uploadedFiles += 1;
        if (formData.translatedFile) {
          const translatedDocUploadResponse = await documentTranslationSaveApi({
            documentId: docUploadResponse.payload.documentId,
            documentType: 'ProductEnhancementDealerGroupDocument',
            languageIsoCode: Language.french,
            formFile: formData.translatedFile,
          });
          if (translatedDocUploadResponse.status === HttpStatus.ok) {
            uploadedFiles += 1;
          }
          totalFiles += 1;
        }
      }
      totalFiles += 1;
    }

    listDealerGroupDocumentsApi.execute((body) => body);

    if (totalFiles !== uploadedFiles) {
      addLog({
        warning: combineStrings(
          ' ',
          `Some files did not get uploaded!`,
          totalFiles > 0 ? `${uploadedFiles}/${totalFiles} files uploaded!` : null
        ),
      });
    } else {
      addLog({
        success: replaceStringTemplateWithValues(lang.wereSuccessfullyUploaded, {
          entity: lang.productEnhancementDocuments,
        }),
      });
    }

    setView(undefined);
  };

  const handleDeleteDealerGroupDocumentSubmit = async () => {
    const productEnhancementDealerGroupDocuments = selectedItems || [];
    const translatedDocs = cloneDeep(selectedTranslatedItems) || [];
    let deletedFiles = 0;
    let totalFiles = 0;

    for (const productEnhancementDealerGroupDocument of productEnhancementDealerGroupDocuments) {
      const docDeleteReponse = await dealerGroupDeleteProductEnhancementDocumentApi({
        productEnhancementDealerGroupDocumentId: productEnhancementDealerGroupDocument.id,
      });
      if (docDeleteReponse.status === HttpStatus.ok) {
        deletedFiles += 1;
      }
      totalFiles += 1;

      const tempDocs = listDocumentTranslationsApi.payload?.data.filter(
        (d) => d.documentId === productEnhancementDealerGroupDocument.id
      );
      const diffDocs = differenceBy(tempDocs, translatedDocs, (it) => it.id);
      translatedDocs.push(...diffDocs);
    }
    for (const translatedDoc of translatedDocs) {
      const translationDocDeleteReponse = await documentTranslationDeleteApi({
        id: translatedDoc.id,
      });
      if (translationDocDeleteReponse.status === HttpStatus.ok) {
        deletedFiles += 1;
      }
      totalFiles += 1;
    }

    listDealerGroupDocumentsApi.execute((body) => body);

    if (totalFiles !== deletedFiles) {
      addLog({
        warning: combineStrings(
          ' ',
          `Some files did not get deleted!`,
          totalFiles > 0 ? `${deletedFiles}/${totalFiles} files deleted!` : null
        ),
      });
    } else {
      addLog({
        success: replaceStringTemplateWithValues(lang.wereSuccessfullyDeleted, {
          entity: lang.productEnhancementDocuments,
        }),
      });
    }

    setView(undefined);
    setSelectedItems([]);
    setSelectedTranslatedItems([]);
  };

  const toggleItem = (productEnhancementDealerGroupDocument: Model.IProductEnhancementDealerGroupDocument) => {
    const newSelectedItemList = xorBy(selectedItems || [], [productEnhancementDealerGroupDocument], 'dealerGroupId');
    setSelectedItems(newSelectedItemList);
  };
  const toggleTranslatedItem = (translatedDoc: Utils.SingleOrArray<Model.IDocumentTranslation>) => {
    const newSelectedItemList = xorBy(selectedTranslatedItems || [], convertToArray(translatedDoc), 'documentId');
    setSelectedTranslatedItems(newSelectedItemList);
  };

  const toggleSelectAll = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedItems(listDealerGroupDocumentsApi.payload?.data ?? []);
    } else {
      setSelectedItems([]);
    }
  };

  const toggleSelectAllTranslations = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedTranslatedItems(listDocumentTranslationsApi.payload?.data ?? []);
    } else {
      setSelectedTranslatedItems([]);
    }
  };

  const [filteredDealerGroupDocuments, filteredDealerGroupDocumentsCount] = useMemo(() => {
    const filteredList = listDealerGroupDocumentsApi.payload?.data?.filter(
      (dealerGroupDocument) => !distributorId || dealerGroupDocument.dealerGroup.distributorId === distributorId
    );

    return [filteredList?.slice(offset * limit, offset * limit + limit), filteredList?.length];
  }, [listDealerGroupDocumentsApi.payload, limit, offset, distributorId]);

  useEffect(() => {
    setSelectedItems([]);
  }, [listDealerGroupDocumentsApi.payload?.data]);

  return {
    currentView,
    setView,
    selectedItems,
    toggleItem,
    toggleSelectAll,
    selectedTranslatedItems,
    toggleTranslatedItem,
    toggleSelectAllTranslations,
    handleDeleteDealerGroupDocumentSubmit,
    handleUploadDealerGroupDocumentSubmit,
    permissions,
    fullDealerGroupDocumentList: listDealerGroupDocumentsApi?.payload?.data,
    translatedDocumentList: listDocumentTranslationsApi?.payload?.data ?? [],
    reloadTranslatedDocs,
    dealerGroupDocumentList: {
      limit,
      offset,
      distributorId,
      setDealerGroupDocumentFilters,
      data: filteredDealerGroupDocuments,
      count: filteredDealerGroupDocumentsCount,
      applyFilters: ({ distributorId }: { distributorId?: number }) => {
        setDealerGroupDocumentFilters((old) => ({ ...old, distributorId }));
      },
      resetFilters: () => {
        setDealerGroupDocumentFilters({ limit: defaultLimit, offset: defaultOffset, distributorId: undefined });
      },
    },
    views: {
      [ProductEnhancementDealerGroupDocumentsAction.DELETE]:
        currentView === ProductEnhancementDealerGroupDocumentsAction.DELETE,
      [ProductEnhancementDealerGroupDocumentsAction.ADD]:
        currentView === ProductEnhancementDealerGroupDocumentsAction.ADD,
    },
  };
}

export function useProductEnhancementDealerGroupDocumentProvider() {
  return useDataProviderPartition<ReturnType<typeof useProductEnhancementDealerGroupDocument>>(
    'productEnhancementDealerGroupDocumentHook'
  );
}
